import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import api from "@/lib/api";
import Select from "./Select";

// Denumiri lizibile pentru entitățile/operațiile scrise de backend în jurnal
// (core/audit.py:log). O entitate/operație nouă fără etichetă aici apare
// oricum, cu numele ei tehnic.
export const ENTITY_LABELS = {
  auth: "Autentificare", user: "Utilizator", user_role: "Rol utilizator",
  user_subrole: "Subrol atribuit", subrole: "Subrol", role_permissions: "Permisiuni pe rol",
  employer: "Angajator", location: "Punct de lucru", org_unit: "Structură organizatorică",
  employment: "Raport de muncă", import: "Import date", platform_config: "Configurare platformă",
  smtp_config: "Configurare e-mail (SMTP)", holiday_calendar: "Sărbători legale",
  signature: "Semnătură autorizată", campaign: "Campanie", annual_plan: "Programare anuală CO",
  leave_period: "Perioadă de concediu", delegation: "Delegare aprobări", report: "Raport",
  employee_document: "Document (Dosar electronic)", employee_document_category: "Categorie documente",
  employee_document_type: "Tip document",
  client: "Client", client_settings: "Comutatoare client", license: "Licență",
  license_plan_price: "Preț plan licență", license_export: "Export licențe",
  billing_snapshot: "Instantaneu facturare", activation: "Activare instalare",
};

export const OPERATION_LABELS = {
  create: "Creare", update: "Modificare", delete: "Ștergere", deactivate: "Dezactivare",
  activate: "Activare", assign: "Atribuire", revoke: "Revocare",
  login: "Autentificare reușită", login_failed: "Autentificare eșuată", login_blocked: "Autentificare blocată (cont blocat temporar)",
  logout: "Deconectare", change_password: "Schimbare parolă", change_password_self: "Schimbare parolă proprie",
  reset_password: "Resetare parolă", upload_signature: "Încărcare semnătură", delete_signature: "Ștergere semnătură",
  upload_logo: "Încărcare logo", excel_import: "Import Excel", test: "Test trimitere",
  seed_ro: "Generare sărbători RO", close: "Închidere", archive: "Arhivare", reopen: "Redeschidere",
  submit: "Trimitere spre aprobare", approve: "Aprobare", reject: "Respingere",
  export_pdf: "Export PDF", export_xlsx: "Export Excel", export: "Export",
  upload: "Încărcare", download: "Descărcare", view: "Vizualizare", share_link_create: "Generare link partajare (eliminat)",
  check_expiry_alerts: "Verificare alerte de expirare",
  set_admin: "Setare cont Admin", fetch_provisioning: "Preluare date de provizionare",
  renew: "Reînnoire / modificare", suspend: "Suspendare", reactivate: "Reactivare",
  generate_kit: "Generare kit de instalare", refresh: "Recalculare", sync_now: "Sincronizare manuală",
};

const entityLabel = (e) => ENTITY_LABELS[e] || e;
const opLabel = (o) => OPERATION_LABELS[o] || o;

// Primul câmp "de identificare" găsit în instantaneu — ce anume a fost afectat.
const SUMMARY_KEYS = ["company_name", "name", "title", "username", "first_name", "cui", "module_key",
  "label", "year", "filename", "original_filename", "to", "format"];
function summarize(l) {
  const snap = { ...(l.before || {}), ...(l.after || {}) };
  if (snap.first_name || snap.last_name) {
    const full = `${snap.first_name || ""} ${snap.last_name || ""}`.trim();
    if (full) return full;
  }
  for (const k of SUMMARY_KEYS) {
    const v = snap[k];
    if (v !== undefined && v !== null && v !== "" && typeof v !== "object") return String(v);
  }
  return "";
}

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Da" : "Nu";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function Details({ l }) {
  const before = l.before || {}, after = l.after || {};
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  const hasBefore = l.before && Object.keys(before).length > 0;
  const hasAfter = l.after && Object.keys(after).length > 0;
  return (
    <div style={{ padding: "10px 14px", background: "#F8FAFC", fontSize: 12 }}>
      <div style={{ color: "#64748B", marginBottom: 6 }}>
        ID obiect: <span style={{ fontFamily: "monospace" }}>{l.entity_id}</span>
      </div>
      {keys.length === 0 ? <div style={{ color: "#94A3B8" }}>Fără detalii suplimentare.</div> : (
        <table className="tbl" style={{ fontSize: 12 }}>
          <thead><tr><th>Câmp</th>{hasBefore && <th>Înainte</th>}{hasAfter && <th>După</th>}</tr></thead>
          <tbody>{keys.map(k => {
            const changed = hasBefore && hasAfter && JSON.stringify(before[k]) !== JSON.stringify(after[k]);
            return (
              <tr key={k} style={changed ? { background: "#FEF9C3" } : undefined}>
                <td style={{ fontFamily: "monospace" }}>{k}</td>
                {hasBefore && <td style={{ wordBreak: "break-all" }}>{fmt(before[k])}</td>}
                {hasAfter && <td style={{ wordBreak: "break-all" }}>{fmt(after[k])}</td>}
              </tr>
            );
          })}</tbody>
        </table>
      )}
    </div>
  );
}

const PAGE = 100;
const EMPTY = { date_from: "", date_to: "", username: "", entity: "", operation: "", q: "" };

export default function AuditLogView({ endpoint, title, subtitle, testid = "audit" }) {
  const [filters, setFilters] = useState(EMPTY);
  const [query, setQuery] = useState(EMPTY);  // filtrele aplicate efectiv
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [facets, setFacets] = useState({ entities: [], operations: [], usernames: [] });
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`${endpoint}/facets`).then(r => setFacets(r.data)).catch(() => {});
  }, [endpoint]);

  useEffect(() => {
    const params = { limit: PAGE, offset: page * PAGE };
    Object.entries(query).forEach(([k, v]) => { if (v) params[k] = v; });
    setLoading(true); setError("");
    api.get(endpoint, { params })
      .then(r => { setData(r.data); setOpen(null); })
      .catch(e => { const d = e.response?.data?.detail; setError(typeof d === "string" ? d : "Nu am putut încărca jurnalul."); })
      .finally(() => setLoading(false));
  }, [endpoint, query, page]);

  const set = (k) => (e) => setFilters(f => ({ ...f, [k]: e.target.value }));
  const apply = (e) => { e?.preventDefault(); setPage(0); setQuery(filters); };
  const reset = () => { setFilters(EMPTY); setPage(0); setQuery(EMPTY); };

  const from = data.total === 0 ? 0 : page * PAGE + 1;
  const to = Math.min((page + 1) * PAGE, data.total);

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />
      <form className="card" onSubmit={apply} style={{ padding: 16, marginBottom: 16 }} data-testid={`${testid}-filters`}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, alignItems: "end" }}>
          <div><label className="label">De la data</label>
            <input type="date" className="input" value={filters.date_from} onChange={set("date_from")} data-testid={`${testid}-date-from`} /></div>
          <div><label className="label">Până la data</label>
            <input type="date" className="input" value={filters.date_to} onChange={set("date_to")} data-testid={`${testid}-date-to`} /></div>
          <div><label className="label">Utilizator</label>
            <Select className="input" value={filters.username} onChange={set("username")} data-testid={`${testid}-user`}>
              <option value="">Toți</option>
              {facets.usernames.map(u => <option key={u} value={u}>{u}</option>)}
            </Select></div>
          <div><label className="label">Obiect</label>
            <Select className="input" value={filters.entity} onChange={set("entity")} data-testid={`${testid}-entity`}>
              <option value="">Toate</option>
              {[...facets.entities].sort((a, b) => entityLabel(a).localeCompare(entityLabel(b), "ro"))
                .map(e => <option key={e} value={e}>{entityLabel(e)}</option>)}
            </Select></div>
          <div><label className="label">Acțiune</label>
            <Select className="input" value={filters.operation} onChange={set("operation")} data-testid={`${testid}-operation`}>
              <option value="">Toate</option>
              {[...facets.operations].sort((a, b) => opLabel(a).localeCompare(opLabel(b), "ro"))
                .map(o => <option key={o} value={o}>{opLabel(o)}</option>)}
            </Select></div>
          <div><label className="label">Caută (ID, IP, detalii)</label>
            <input className="input" value={filters.q} onChange={set("q")} placeholder="ex: CUI, nume, IP" data-testid={`${testid}-q`} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button type="submit" className="btn btn-primary" data-testid={`${testid}-apply`}>Filtrează</button>
          <button type="button" className="btn btn-secondary" onClick={reset}>Resetează</button>
        </div>
      </form>

      <div className="card">
        <div className="flex-between" style={{ padding: "10px 14px", fontSize: 13, color: "#64748B" }}>
          <span data-testid={`${testid}-count`}>{loading ? "Se încarcă…" : `${from}–${to} din ${data.total} evenimente`}</span>
          <span style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }}
              disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>‹ Anterioare</button>
            <button className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }}
              disabled={to >= data.total || loading} onClick={() => setPage(p => p + 1)}>Următoare ›</button>
          </span>
        </div>
        {error && <div style={{ padding: "0 14px 10px", color: "#B91C1C" }}>{error}</div>}
        <table className="tbl">
          <thead><tr><th>Data/Ora</th><th>Utilizator</th><th>Adresă IP</th><th>Acțiune</th><th>Obiect</th><th></th></tr></thead>
          <tbody>{data.items.map(l => (
            <React.Fragment key={l.id}>
              <tr data-testid={`${testid}-row`}>
                <td className="tabular-nums" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{new Date(l.at).toLocaleString("ro-RO")}</td>
                <td>{l.username || "—"}</td>
                <td className="tabular-nums" style={{ fontSize: 12, fontFamily: "monospace" }}>{l.ip || "—"}</td>
                <td><span className={`badge ${l.operation === "login_failed" || l.operation === "login_blocked" ? "badge-rejected" : "badge-active"}`}>{opLabel(l.operation)}</span></td>
                <td>{entityLabel(l.entity)}{summarize(l) && <span style={{ color: "#64748B" }}> · {summarize(l)}</span>}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: 12 }}
                    onClick={() => setOpen(open === l.id ? null : l.id)} data-testid={`${testid}-details`}>
                    {open === l.id ? "Ascunde" : "Detalii"}
                  </button>
                </td>
              </tr>
              {open === l.id && <tr><td colSpan={6} style={{ padding: 0 }}><Details l={l} /></td></tr>}
            </React.Fragment>
          ))}</tbody>
        </table>
        {!loading && data.items.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "#94A3B8" }}>Niciun eveniment.</div>}
      </div>
    </>
  );
}
